/* eslint no-use-before-define: ["error", { "variables": false }] */

import { StyleSheet, View } from 'react-native-web';

import Actions from './Actions';
import Color from './Color';
import Composer from './Composer';
import InputToolbarButton from './InputToolbarButton';
import PropTypes from 'prop-types';
import React from 'react';
import Send from './Send';
import { MIN_COMPOSER_HEIGHT, MAX_COMPOSER_HEIGHT } from './Constant';

export default class InputToolbar extends React.Component {
  renderActions() {
    if (this.props.renderActions) {
      return this.props.renderActions(this.props);
    }
    if (this.props.onPressActionButton) {
      return (
        <View style={styles.actionsContainer}>
          <Actions {...this.props} label={this.props.renderActionLabel} disabled={this.props.isInputDisabled} />
        </View>
      );
    }
    return null;
  }

  renderEmojiButton() {
    if (this.props.onPressEmojiButton) {
      return (
        <View style={styles.emojiButtonContainer}>
          <InputToolbarButton
            onPress={this.props.onPressEmojiButton}
            renderIcon={this.props.renderEmojiIcon}
            renderHoverIcon={this.props.renderEmojiHoverIcon}
            label={this.props.renderEmojiLabel}
            testID='emoji-button'
            accessibilityLabel='Emoji'
            disabled={this.props.isInputDisabled}
          />
        </View>
      );
    }
    return null;
  }

  renderVoiceButton() {
    const { isVoiceMode, onPressVoiceButton, renderVoiceIcon, renderVoiceHoverIcon, text, renderVoiceLabel } =
      this.props;

    if (isVoiceMode) {
      return null;
    }

    if (text && text.trim().length > 0) {
      return null;
    }

    if (onPressVoiceButton && renderVoiceIcon) {
      return (
        <View style={styles.voiceButtonContainer}>
          <InputToolbarButton
            onPress={onPressVoiceButton}
            renderIcon={renderVoiceIcon}
            renderHoverIcon={renderVoiceHoverIcon}
            label={renderVoiceLabel}
            containerStyle={styles.voiceButtonInner}
            testID='voice-button'
            accessibilityLabel='Voice Input'
            disabled={this.props.isInputDisabled}
          />
        </View>
      );
    }

    return null;
  }

  renderKeyboardButton() {
    const { isVoiceMode, onPressVoiceButton, renderKeyboardIcon, renderKeyboardHoverIcon, renderKeyboardLabel } =
      this.props;

    if (!isVoiceMode) {
      return null;
    }

    if (onPressVoiceButton && renderKeyboardIcon) {
      return (
        <View style={styles.voiceButtonContainer}>
          <InputToolbarButton
            onPress={onPressVoiceButton}
            renderIcon={renderKeyboardIcon}
            renderHoverIcon={renderKeyboardHoverIcon}
            label={renderKeyboardLabel}
            testID='keyboard-button'
            accessibilityLabel='Keyboard Input'
            disabled={this.props.isInputDisabled}
          />
        </View>
      );
    }

    return null;
  }

  renderSend() {
    const { isVoiceMode, text } = this.props;

    if (isVoiceMode) {
      return null;
    }

    if (!text || text.trim().length === 0) {
      return null;
    }

    if (this.props.renderSend) {
      return this.props.renderSend(this.props);
    }
    return <Send {...this.props} disabled={this.props.isInputDisabled} />;
  }

  renderComposer() {
    if (this.props.renderComposer) {
      return this.props.renderComposer(this.props);
    }

    return <Composer {...this.props} onPaste={this.props.onPaste} />;
  }

  renderAccessory() {
    if (this.props.renderAccessory) {
      return (
        <View style={[styles.accessory, this.props.accessoryStyle]}>{this.props.renderAccessory(this.props)}</View>
      );
    }
    return null;
  }

  render() {
    // Stay in normal document flow on web. RN Keyboard absolute positioning
    // reserved composerHeight under the controls and left a blank band.
    const toolbarHeight = this.props.composerHeight || MIN_COMPOSER_HEIGHT;
    return (
      <View
        style={[
          styles.container,
          this.props.containerStyle,
          {
            height: toolbarHeight,
            minHeight: MIN_COMPOSER_HEIGHT,
            maxHeight: MAX_COMPOSER_HEIGHT
          }
        ]}
      >
        <View style={[styles.primary, this.props.primaryStyle]}>
          {this.renderActions()}
          {this.renderEmojiButton()}
          {this.renderComposer()}
          {this.renderVoiceButton()}
          {this.renderKeyboardButton()}
          {this.renderSend()}
        </View>
        {this.renderAccessory()}
      </View>
    );
  }
}

const styles = StyleSheet.create({
  container: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-end',
    flexShrink: 0,
    backgroundColor: Color.canvas,
    paddingHorizontal: 15,
    boxSizing: 'border-box',
    overflow: 'hidden'
  },
  primary: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    paddingTop: 2,
    paddingBottom: 0
  },
  accessory: {
    height: 44
  },
  emojiButtonContainer: {
    width: 26,
    minHeight: 26,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'flex-start'
  },
  actionsContainer: {
    marginRight: 15
  },
  voiceButtonContainer: {
    minHeight: 40,
    minWidth: 44,
    justifyContent: 'flex-start',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    marginLeft: 12,
    flexShrink: 0
  },
  // Wider than default 26px so "Voice"/"V" label doesn't sit on the composer border.
  voiceButtonInner: {
    width: 44,
    minWidth: 44
  }
});

InputToolbar.defaultProps = {
  renderAccessory: null,
  renderActions: null,
  renderSend: null,
  renderSendIcon: null,
  renderComposer: null,
  containerStyle: {},
  primaryStyle: {},
  accessoryStyle: {},
  onPressActionButton: () => {},
  onPressEmojiButton: null,
  renderEmojiIcon: null,
  renderEmojiHoverIcon: null,
  renderEmojiLabel: null,
  onPaste: null,
  isVoiceMode: false,
  onPressVoiceButton: null,
  isInputDisabled: false,
  renderVoiceIcon: null,
  renderVoiceHoverIcon: null,
  renderVoiceLabel: null,
  renderKeyboardIcon: null,
  renderKeyboardHoverIcon: null,
  renderKeyboardLabel: null
};

InputToolbar.propTypes = {
  renderAccessory: PropTypes.func,
  renderActions: PropTypes.func,
  renderSend: PropTypes.func,
  renderSendIcon: PropTypes.func,
  renderComposer: PropTypes.func,
  onPressActionButton: PropTypes.func,
  onPressEmojiButton: PropTypes.func,
  renderEmojiIcon: PropTypes.func,
  renderEmojiHoverIcon: PropTypes.func,
  renderEmojiLabel: PropTypes.string,
  onPaste: PropTypes.func,
  containerStyle: PropTypes.object,
  primaryStyle: PropTypes.object,
  accessoryStyle: PropTypes.object,
  composerHeight: PropTypes.number,
  isVoiceMode: PropTypes.bool,
  onPressVoiceButton: PropTypes.func,
  isInputDisabled: PropTypes.bool,
  renderVoiceIcon: PropTypes.func,
  renderVoiceHoverIcon: PropTypes.func,
  renderVoiceLabel: PropTypes.string,
  renderKeyboardIcon: PropTypes.func,
  renderKeyboardHoverIcon: PropTypes.func,
  renderKeyboardLabel: PropTypes.string
  /*
  containerStyle: ViewPropTypes.style,
  primaryStyle: ViewPropTypes.style,
  accessoryStyle: ViewPropTypes.style,
  */
};
